import { useEffect } from 'react'
import type { JSX } from 'react'
import { Link, useParams, Outlet } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import {
  AllChannelsIcon,
  ArrowRightIcon,
  Button,
  ChannelAvatar,
  EmptyState,
  ExternalLinkIcon,
  ListFilterIcon,
  Flex,
  IconButton,
  LockIcon,
  PageHeader,
  PageLayout,
  pageHeaderStyles,
  PlusIcon,
  Skeleton,
  SkeletonText,
} from '@buffer-mono/popcorn'

import { ChannelError } from '~publish/components/ChannelError'
import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { FilterByPostType } from '~publish/components/FilterByPostType'
import { POST_TYPE_FILTER_SPLIT } from '~publish/helpers/postType'
import {
  FilterByTag,
  filterOutUntaggedId,
} from '~publish/components/FilterByTag'
import { LinkedInCapabilitiesLaunchDialog } from '~publish/components/LinkedInCapabilitiesLaunchDialog'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { PageNotices, StickyPageNotice } from '~publish/components/PageNotices'
import { PostListOrCalendarViewToggle } from '~publish/components/PostListOrCalendarViewToggle/PostListOrCalendarViewToggle'
import {
  PostEmptyStateByTab,
  PostTabs,
  PostTabsResponsive,
} from '~publish/components/PostTabs'
import type { PostTab } from '~publish/components/PostTabs'
import { RegisteredBannersProvider } from '~publish/components/RegisteredBanner'
import { StartPageSunsetNotice } from '~publish/components/StartPageSunsetNotice'
import { TimezoneDisplay } from '~publish/components/TimezoneDisplay'
import { ViewOnlyHeaderBadge } from '~publish/components/ViewOnlyBadge'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { buildStartPageEditorUrl } from '~publish/helpers/externalUrls'
import { postStatusByTab, mapPostStatusToTab } from '~publish/helpers/post'
import { trackQueueOpened } from '~publish/helpers/queueCalendarTracking'
import { usePostTypeFilter } from '~publish/hooks/usePostTypeFilter'
import { toQueryParamArray, useQueryParam } from '~publish/hooks/useQueryParam'
import { TimezoneProvider } from '~publish/hooks/useTimezone'
import { allChannels, settingsChannelsRoute } from '~publish/legacy/routes'
import { useFullStory } from '~publish/legacy/thirdParty/hooks/useFullStory'
import { canPerform } from '~publish/utils/permissions/canPerform'
import { isViewOnlyPublishChannel } from '~publish/utils/permissions/viewOnlyPublish'

// TODO: fetching post counts and list should be extracted to common folders
import { ChannelAccessDenied } from './ChannelAccessDenied'
import { ChannelProvider, useSelectedChannel } from './ChannelContext'
import { ChannelPageTitle } from './ChannelPageTitle'
import { ChannelSettingsMenu } from './ChannelSettingsMenu'
import { GridPreviewButton } from './ChannelShopGrid/GridPreviewButton'
import { DisconnectedChannelNotice } from './DisconnectedChannelNotice'
import { MoreActions } from './MoreActions'
import { QueueLimitNotice } from './QueueLimitNotice'
import { QueueList } from './QueueList'
import { QueueList_Channel } from './QueueList/fragments/QueueList_Channel'
import { QueuePausedNotice } from './QueuePausedNotice'
import { XRefreshBanner } from './XRefreshBanner'
import { PostList, PostListSkeleton } from '../AllChannels/PostList'
import { usePostCounts } from '../AllChannels/PostList/usePostCounts'

import styles from './ChannelPage.module.css'

const SECTION_HEADING_BY_POST_TAB: Record<Exclude<PostTab, 'queue'>, string> = {
  drafts: 'Drafts',
  approvals: 'Approvals',
  sent: 'Sent',
}

export const ChannelPage_Channel = graphql(
  /* GraphQL */ `
    fragment ChannelPage_Channel on Channel {
      ...QueueList_Channel
      allowedActions
      id
      createdAt
      avatar
      isQueuePaused
      isDisconnected
      isLocked
      name
      displayName
      organizationId
      serviceId
      metadata {
        ... on BlueskyMetadata {
          __typename
          serverUrl
        }
        ... on MastodonMetadata {
          __typename
          maxCharacters
        }
        ... on TwitterMetadata {
          __typename
          subscriptionType
        }
        ... on LinkedInMetadata {
          __typename
          shouldShowLinkedinAnalyticsRefreshBanner
        }
        ... on PinterestMetadata {
          __typename
          boards {
            id
            serviceId
            url
            name
            avatar
          }
        }
      }
      locationData {
        location
      }
      hasActiveMemberDevice
      serverUrl
      service
      timezone
      type
      apiVersion
      postingSchedule {
        day
        times
        paused
      }
      postingGoal {
        goal
        sentCount
        scheduledCount
        periodStart
        periodEnd
        status
      }
      weeklyPostingLimit {
        limit
        sent
        scheduled
      }
    }
  `,
  [QueueList_Channel],
)
export const GetChannelInfo = graphql(
  /* GraphQL */ `
    query GetChannelInfo($channelId: ChannelId!) {
      channel(input: { id: $channelId }) {
        ...QueueList_Channel
        ...ChannelPage_Channel
        id
        avatar
        products
        allowedActions
        isQueuePaused
        isLocked
        name
        displayName
        locationData {
          location
        }
        serverUrl
        service
        timezone
        type
        apiVersion
      }
    }
  `,
  [QueueList_Channel, ChannelPage_Channel],
)

const availableTabs: ReadonlySet<PostTab> = new Set([
  'queue',
  'drafts',
  'approvals',
  'sent',
])

export const ChannelPage = (): JSX.Element => {
  const id = useParams<{ id: string }>().id || ''
  const [tab = 'queue', setTab] = useQueryParam<PostTab>('tab')
  const [rawTagsQueryParam, setTagFilter] = useQueryParam<string[]>('tags')
  const tagsQueryParam = toQueryParamArray(rawTagsQueryParam)
  const { initFullStory } = useFullStory()

  const organization = useCurrentOrganization()
  const organizationId = organization?.id
  const channelsCount = organization?.channels.length

  const shouldBeSticky = true

  const { data, error } = useQuery(GetChannelInfo, {
    variables: {
      channelId: id,
    },
    fetchPolicy: 'cache-and-network',
    skip: !organizationId,
  })
  const channel = data?.channel
  const canViewChannel = channel ? canPerform(channel, 'viewPublish') : false

  const {
    isEnabled: isPostTypeFilterEnabled,
    options: postTypeOptions,
    selected: selectedPostTypes,
    setSelected: setPostTypeFilter,
  } = usePostTypeFilter(channel?.service)

  const { counts, loading: countsLoading } = usePostCounts({
    organizationId,
    channelIds: [id],
    tagIds: tagsQueryParam,
    postTypes: selectedPostTypes,
  })

  useEffect(
    function checkTabQueryParam() {
      if (!availableTabs.has(tab)) {
        const postStatus = Object.values(postStatusByTab)
          .flat()
          .find((status) => status === String(tab))
        setTab(postStatus ? mapPostStatusToTab(postStatus) : 'queue')
      }
    },
    [setTab, tab],
  )

  // tracking
  useEffect(() => {
    initFullStory()
  }, [initFullStory])

  useEffect(() => {
    if (tab !== 'queue' || !organizationId || !channel?.id || !canViewChannel) {
      return
    }

    trackQueueOpened(organizationId)
  }, [canViewChannel, channel?.id, organizationId, tab])

  // HOTFIX: this event was causing aa lot of traffic and us hitting Mixpanel limits
  // TODO: Create different tracking for filters and tab switches
  // See: https://linear.app/buffer/issue/CONT-158
  // useEffect(() => {
  //   BufferTracker.singleChannelOpened({
  //     tab,
  //     organizationId,
  //     accountId: accountId || '',
  //     channelSelected: id,
  //     tagsSelected: tagsQueryParam.length === 0 ? undefined : tagsQueryParam,
  //   })
  // }, [organizationId, id, tagsQueryParam, tab, accountId])

  if (error) {
    return <ChannelError error={error} />
  }

  if (!channel) {
    return (
      <PageLayout>
        <PageHeader>
          <PageHeader.Row>
            <Flex direction="row" align="center" gap="md">
              <Skeleton
                rounded
                width={48}
                height={48}
                className={styles.skeletonAvatar}
              />
              <Skeleton rounded width={100} height={20} />
            </Flex>
            <PageHeader.Actions>
              <Skeleton width={90} height={40} />
            </PageHeader.Actions>
          </PageHeader.Row>
          <PageHeader.Row>
            <Flex direction="row" align="center" gap="md">
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
            </Flex>
            <PageHeader.Actions>
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
              <SkeletonText lines={1} width={80} />
            </PageHeader.Actions>
          </PageHeader.Row>
        </PageHeader>
        <PageLayout.Container size="narrow" className={styles.content}>
          <PostListSkeleton />
        </PageLayout.Container>
      </PageLayout>
    )
  }

  if (channel.isLocked) {
    return <ChannelLockedError channel={channel} />
  }

  // `viewPublish` is the publish-access gate for the Publish surface. A
  // comments-only member holds `readUpdates` (so `findUpdates` authorizes
  // loading this channel's posts) but not `viewPublish`, so a deep-linked or
  // saved-view URL would otherwise render the queue. Mirror the All Channels
  // Access Denied state — and the sibling single-channel gates (`viewComments`
  // in Community, `viewInsights` in Insights) — and keep Publish publish-only.
  if (!canViewChannel) {
    return <ChannelAccessDenied channel={channel} />
  }

  const tabs: { id: PostTab; count: number | null | undefined }[] = [
    { id: 'queue', count: counts?.queue },
    { id: 'drafts', count: counts?.drafts },
    { id: 'approvals', count: counts?.approvals },
    { id: 'sent', count: counts?.sent },
  ]

  const tabCountLimit = counts?.limit

  const filterApplied =
    tagsQueryParam.length > 0 || selectedPostTypes.length > 0

  const isViewOnly = isViewOnlyPublishChannel(channel)

  return (
    <>
      <Outlet />
      <LinkedInCapabilitiesLaunchDialog />
      <ChannelProvider channel={channel}>
        <TimezoneProvider timeZone={channel.timezone} key={channel.id}>
          <RegisteredBannersProvider
            key={`registered-banner-provider-${channel.name}`}
          >
            <PostTabs asChild value={tab} onChange={setTab}>
              <PageLayout>
                <PageNotices>
                  {!shouldBeSticky && <QueueLimitNotice />}
                  <ChannelPageBanners />
                </PageNotices>
                {shouldBeSticky && (
                  <StickyPageNotice>
                    <QueueLimitNotice />
                  </StickyPageNotice>
                )}
                <PageHeader>
                  <PageHeader.Row>
                    <ChannelPageTitle />
                    <PageHeader.Actions>
                      {isViewOnly && <ViewOnlyHeaderBadge />}
                      <FeedbackWidget
                        id="single-channel-1"
                        className={pageHeaderStyles.hideOnNarrow}
                      />
                      <GridPreviewButton
                        className={pageHeaderStyles.hideOnNarrow}
                        size="medium"
                      />
                      <div className={pageHeaderStyles.hideOnNarrow}>
                        <PostListOrCalendarViewToggle
                          className={styles.toggleViewAction}
                        />
                      </div>
                      {!isViewOnly && (
                        <NewPostComposerTrigger
                          cta="publish-singleChannel-header-newPost-1"
                          channels={[id]}
                          prefillPostData={{
                            tags: filterOutUntaggedId(tagsQueryParam),
                          }}
                          className={pageHeaderStyles.hideOnNarrow}
                        >
                          <Button
                            data-testid="queue-header-create-post"
                            size="medium"
                            variant="secondary"
                          >
                            <PlusIcon /> New Post
                          </Button>
                        </NewPostComposerTrigger>
                      )}
                      {!isViewOnly && (
                        <ChannelSettingsMenu
                          channelId={id}
                          service={channel.service}
                          channelType={channel.type}
                          className={pageHeaderStyles.hideOnWide}
                        />
                      )}
                      {!isViewOnly && (
                        <NewPostComposerTrigger
                          cta="publish-singleChannel-header-newPost-1"
                          channels={[id]}
                          prefillPostData={{
                            tags: filterOutUntaggedId(tagsQueryParam),
                          }}
                          className={pageHeaderStyles.hideOnWide}
                        >
                          <IconButton
                            variant="primary"
                            label="New Post"
                            size="large"
                          >
                            <PlusIcon />
                          </IconButton>
                        </NewPostComposerTrigger>
                      )}
                    </PageHeader.Actions>
                  </PageHeader.Row>
                  <PageHeader.Row>
                    <PostTabsResponsive
                      tabs={tabs}
                      value={tab}
                      onChange={setTab}
                      countLimit={tabCountLimit}
                      countLoading={countsLoading}
                    />
                    <PageHeader.Actions>
                      <FilterByTag
                        value={tagsQueryParam}
                        onSelect={setTagFilter}
                        className={pageHeaderStyles.hideOnMedium}
                      />
                      {isPostTypeFilterEnabled && (
                        <FilterByPostType
                          options={postTypeOptions}
                          value={selectedPostTypes}
                          onSelect={setPostTypeFilter}
                          splitName={POST_TYPE_FILTER_SPLIT}
                          className={pageHeaderStyles.hideOnMedium}
                        />
                      )}

                      <TimezoneDisplay
                        channelId={id}
                        timezone={channel.timezone}
                        disabled={isViewOnly}
                        className={pageHeaderStyles.hideOnMedium}
                      />
                      <MoreActions
                        channelId={id}
                        selectedTags={tagsQueryParam}
                        onTagSelect={setTagFilter}
                        postTypeOptions={postTypeOptions}
                        selectedPostTypes={selectedPostTypes}
                        onPostTypeSelect={setPostTypeFilter}
                        timezone={channel.timezone}
                        className={pageHeaderStyles.hideOnNarrow}
                      />
                      <MoreActions
                        channelId={id}
                        selectedTags={tagsQueryParam}
                        onTagSelect={setTagFilter}
                        postTypeOptions={postTypeOptions}
                        selectedPostTypes={selectedPostTypes}
                        onPostTypeSelect={setPostTypeFilter}
                        timezone={channel.timezone}
                        triggerIcon={<ListFilterIcon />}
                        className={pageHeaderStyles.hideOnWide}
                      />
                      <div className={pageHeaderStyles.hideOnWide}>
                        <PostListOrCalendarViewToggle />
                      </div>
                    </PageHeader.Actions>
                  </PageHeader.Row>
                </PageHeader>

                <PageLayout.Container size="narrow" className={styles.content}>
                  <PostTabs.Panel value="queue" asChild>
                    <QueueList
                      key={`${id}-queue`}
                      channel={channel}
                      tagIds={
                        tagsQueryParam.length ? tagsQueryParam : undefined
                      }
                      postTypes={
                        selectedPostTypes.length ? selectedPostTypes : undefined
                      }
                    />
                  </PostTabs.Panel>

                  {(['drafts', 'approvals', 'sent'] as const).map((postTab) => (
                    <PostTabs.Panel
                      key={`${id}-${postTab}`}
                      value={postTab}
                      asChild
                    >
                      <PostList
                        key={`${id}-${postTab}`}
                        status={postTab}
                        sectionHeading={SECTION_HEADING_BY_POST_TAB[postTab]}
                        channelIds={[id]}
                        // Content items have no channel, so a channel-less
                        // draft only has a home here when this is the org's
                        // one and only channel.
                        includeChannellessDrafts={
                          postTab === 'drafts' && channelsCount === 1
                        }
                        tagIds={
                          tagsQueryParam.length ? tagsQueryParam : undefined
                        }
                        postTypes={
                          selectedPostTypes.length
                            ? selectedPostTypes
                            : undefined
                        }
                        emptyState={
                          !filterApplied && (
                            <PostEmptyStateByTab tab={postTab}>
                              {!isViewOnly && (
                                <>
                                  <NewPostComposerTrigger
                                    cta="publish-singleChannel-emptyState-newPost-1"
                                    channels={[id]}
                                  >
                                    <Button size="large">
                                      <PlusIcon /> New Post
                                    </Button>
                                  </NewPostComposerTrigger>
                                  {channelsCount === 1 && (
                                    <Button
                                      as={Link}
                                      to={settingsChannelsRoute.route}
                                      variant="secondary"
                                      size="large"
                                      className={styles.connectMoreButton}
                                    >
                                      <AllChannelsIcon /> Connect more channels
                                    </Button>
                                  )}
                                </>
                              )}
                            </PostEmptyStateByTab>
                          )
                        }
                      />
                    </PostTabs.Panel>
                  ))}
                </PageLayout.Container>
              </PageLayout>
            </PostTabs>
          </RegisteredBannersProvider>
        </TimezoneProvider>
      </ChannelProvider>
    </>
  )
}

export function ChannelPageNotifications(): JSX.Element {
  const selectedChannel = useSelectedChannel()

  return (
    <>
      {/* Notices - displayed in header for sticky behavior */}
      <QueueLimitNotice />
      {selectedChannel?.isQueuePaused && <QueuePausedNotice />}
      <DisconnectedChannelNotice />
      {selectedChannel?.service === 'startPage' && <StartPageSunsetNotice />}

      {/* Banners - First rendered has highest priority */}
      <XRefreshBanner />
    </>
  )
}

export function ChannelPageBanners(): JSX.Element {
  const selectedChannel = useSelectedChannel()

  return (
    <>
      {/* Banners - First rendered has highest priority */}
      {selectedChannel?.isQueuePaused && <QueuePausedNotice />}
      <DisconnectedChannelNotice />
      {selectedChannel?.service === 'startPage' && <StartPageSunsetNotice />}
      <XRefreshBanner />
    </>
  )
}

const GetStartPagePublishStatus = graphql(/* GraphQL */ `
  query GetStartPagePublishStatus($organizationId: String!, $pageId: String) {
    startPage(organizationId: $organizationId, pageId: $pageId) {
      ... on StartPage {
        id
        isPublished
      }
      ... on CommonError {
        message
      }
    }
  }
`)

const ChannelLockedError = ({
  channel,
}: {
  channel: {
    id: string
    service: string
    name?: string | null
    avatar?: string | null
    organizationId?: string | null
  }
}): JSX.Element => {
  const isStartPage = channel.service === 'startPage'

  const { data, loading } = useQuery(GetStartPagePublishStatus, {
    variables: {
      organizationId: channel.organizationId ?? '',
      pageId: channel.id,
    },
    skip: !isStartPage || !channel.organizationId,
  })

  const isUnpublishedStartPage =
    isStartPage &&
    !loading &&
    data?.startPage?.__typename === 'StartPage' &&
    !data.startPage.isPublished

  if (isStartPage && loading) {
    return (
      <Flex align="center" justify="center" fullHeight fullWidth>
        <Flex direction="column" align="center" gap="md">
          <Skeleton width={64} height={64} rounded />
          <SkeletonText lines={1} width={240} />
        </Flex>
      </Flex>
    )
  }

  if (isUnpublishedStartPage) {
    return (
      <Flex align="center" justify="center" fullHeight fullWidth>
        <EmptyState size="xlarge" variant="warning">
          <ChannelAvatar
            channel="startPage"
            src={channel.avatar ?? undefined}
            alt=""
            size="large"
          />
          <EmptyState.Heading>
            {channel.name ?? 'Your'} Start Page is unpublished
          </EmptyState.Heading>
          <EmptyState.Description>
            Publish your Start Page from the Editor to start scheduling posts.
          </EmptyState.Description>
          <EmptyState.Actions>
            <Button
              as="a"
              href={buildStartPageEditorUrl(channel.id)}
              target="_blank"
              rel="noopener noreferrer"
              size="large"
            >
              Open Editor <ExternalLinkIcon />
            </Button>
          </EmptyState.Actions>
        </EmptyState>
      </Flex>
    )
  }

  return (
    <EmptyState size="large" variant="warning">
      <EmptyState.Icon>
        <LockIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>This channel is locked</EmptyState.Heading>
      <EmptyState.Description>
        Channel you are trying to access is locked. Likely it is because your
        account was downgraded.
      </EmptyState.Description>
      <EmptyState.Actions>
        <Button as={Link} to={allChannels.route} size="large">
          <ArrowRightIcon />
          Go back to Publish
        </Button>
      </EmptyState.Actions>
    </EmptyState>
  )
}
